/**
 * The central backend. Used whenever a production build has no explicit
 * REACT_APP_API_URL: the desktop app no longer runs a backend of its own, so
 * falling back to localhost would ship a UI that reaches nothing.
 */
const CENTRAL_API_URL = "https://track.worksuites.online";

function defaultApiBaseUrl() {
  return process.env.NODE_ENV === "production" ? CENTRAL_API_URL : "http://localhost:5000";
}

function normalizeApiBaseUrl(raw) {
  const trimmed = String(raw || "").trim().replace(/\/+$/, "");
  if (!trimmed) {
    return defaultApiBaseUrl();
  }
  try {
    const parsed = new URL(trimmed);
    const currentHost =
      typeof window !== "undefined" && window.location
        ? window.location.hostname
        : "";
    // Only redirect to localhost in dev — packaged Electron also loads from localhost
    // but should use the baked-in production URL.
    const isLocalFrontend = ["localhost", "127.0.0.1"].includes(currentHost);
    const isLocalApi = ["localhost", "127.0.0.1"].includes(parsed.hostname);
    if (process.env.NODE_ENV !== "production" && isLocalFrontend && !isLocalApi) {
      return "http://localhost:5000";
    }
    return trimmed;
  } catch {
    return defaultApiBaseUrl();
  }
}

/** Backend root (Express). CRA only exposes REACT_APP_* — use REACT_APP_API_URL; REACT_APP_API is accepted as an alias. */
export const API_URL = normalizeApiBaseUrl(
  process.env.REACT_APP_API_URL || process.env.REACT_APP_API || process.env.REACT_APP_SERVER_URL
);

export const COMPANY_OPTIONS = ["Digitechio", "Adceptive Media"];

/** Per-company display timezone for clock-in/out (Digitechio: Pakistan, Adceptive Media: India). Keep in sync with backend/src/routes/dashboard.js COMPANY_TIMEZONE_MAP. */
export const COMPANY_TIMEZONE_MAP = {
  "adceptive media": "Asia/Kolkata",
};
export const DEFAULT_COMPANY_TIMEZONE = "Asia/Karachi";

export const ROLE_OPTIONS = ["co-owner", "admin", "manager", "employee"];

export const EMPLOYEE_SCREEN_ROUTES = {
  "my-activities": "/my-activities",
};

export const EMPLOYEE_ROUTE_TO_SCREEN = {
  "/my-activities": "my-activities",
};

export const SCREEN_ROUTES = {
  overview: "/overview",
  team: "/team",
  "my-activities": "/my-activities",
  timesheet: "/timesheet",
  "review-apps": "/review-apps",
  screenshots: "/screenshots",
  "work-times": "/work-times",
  productivity: "/productivity",
  "apps-summary": "/apps-summary",
  employees: "/employees",
  "assign-managers": "/assign-managers",
  settings: "/settings",
};

export const ROUTE_TO_SCREEN = {
  "/overview": "overview",
  "/team": "team",
  "/my-activities": "my-activities",
  "/timesheet": "timesheet",
  "/review-apps": "review-apps",
  "/screenshots": "screenshots",
  "/work-times": "work-times",
  "/productivity": "productivity",
  "/apps-summary": "apps-summary",
  "/employees": "employees",
  "/assign-managers": "assign-managers",
  "/settings": "settings",
};

export const EMPTY_OVERVIEW_CARDS = [
  { label: "Employees", value: 0, tone: "blue" },
  { label: "Working", value: 0, tone: "green" },
  { label: "On break", value: 0, tone: "cyan" },
  { label: "Idle", value: 0, tone: "pink" },
  { label: "Stopped work", value: 0, tone: "rose" },
  { label: "Yet to start", value: 0, tone: "amber" },
];

export const INITIAL_EMPLOYEE_FORM = {
  name: "",
  surname: "",
  email: "",
  company: "",
  team: "",
  role: "",
  password: "",
};

export const DEFAULT_TEAM_DATA = {
  members: [],
  filters: [],
};

export const DEFAULT_TIMESHEET_ROWS = [];
export const DEFAULT_MY_ACTIVITIES = {
  dateLabel: "",
  expectedWorkLabel: "00 hrs. 00 mins.",
  remainingWorkLabel: "00 hrs. 00 mins.",
  totalCalculatedLabel: "00 hrs. 00 mins.",
  totalCalculatedWorkLabel: "00 hrs. 00 mins.",
  totals: {
    workingSeconds: 0,
    breakSeconds: 0,
    idleSeconds: 0,
    activityLevelPercent: 0,
  },
  graph: {
    bucketMinutes: 15,
    points: [],
  },
  history: [],
};

export const DEFAULT_REVIEW_APPS = {
  unreviewedCount: 0,
  reviewedCount: 0,
  tabTotal: 0,
  rows: [],
};

export const DEFAULT_SCREENSHOT_ROWS = [];
export const DEFAULT_WORK_TIMES = {
  dateLabel: "",
  totalCalculatedLabel: "00 hrs. 00 mins.",
  summary: [],
  teams: [],
};
export const DEFAULT_PRODUCTIVITY = {
  dateLabel: "",
  totalCalculatedLabel: "00 hrs. 00 mins.",
  summary: [],
  teams: [],
};
export const DEFAULT_APPS_SUMMARY = {
  dateLabel: "",
  totalCalculatedLabel: "00 hrs. 00 mins.",
  summary: [],
  teams: [],
};

export const ROLE_LABELS = {
  "co-owner": "Owner",
  admin: "Admin",
  manager: "Manager",
  employee: "Employee",
};

export const DEFAULT_TRACK_SETTINGS = {
  workingDays: ["mon", "tue", "wed", "thu", "fri"],
  expectedWorkHours: "09:00",
  minimumProductiveHours: "09:00",
  expectedClockIn: "10:00",
  expectedClockOut: "19:00",
  idleInterval: "2",
  timezone: "Asia/Karachi",
  screenCaptureEnabled: true,
  screenCaptureInterval: "5",
  appUsageInterval: "2",
  trackingCondition: "apps_and_urls",
  alwaysWorking: [],
  alwaysOnBreak: [],
  workNotes: [],
};

export const EMPTY_ORGANIZATION_SETTINGS = {
  organizationName: "",
  organizationId: "",
  industry: "",
  referralSource: "",
  organizationType: "individual",
  fullName: "",
  identityNumber: "",
  country: "",
  zipCode: "",
  invoiceAddress: "",
  companies: [...COMPANY_OPTIONS],
  departments: [],
  departmentsByCompany: Object.fromEntries(COMPANY_OPTIONS.map((company) => [company, []])),
  isClosed: false,
  track: { ...DEFAULT_TRACK_SETTINGS },
};

export const ORGANIZATION_SETTING_TABS = [
  { id: "profile", label: "Profile" },
  { id: "account", label: "My account" },
  { id: "users", label: "Users & departments" },
  { id: "track", label: "Track settings" },
];

/** Minimum self-service password strength; mirrors backend/src/utils/passwordPolicy.js. */
export const MIN_PASSWORD_LENGTH = 8;

export function validatePasswordStrength(password) {
  const value = String(password || "");
  if (value.length < MIN_PASSWORD_LENGTH) {
    return `New password must be at least ${MIN_PASSWORD_LENGTH} characters long.`;
  }
  if (!/[A-Za-z]/.test(value)) {
    return "New password must include at least one letter.";
  }
  if (!/[0-9]/.test(value)) {
    return "New password must include at least one number.";
  }
  return "";
}

export const EMPLOYEE_STATUS_TABS = [
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
];
